import { createContext, useContext, useEffect, useState } from "react";
import { authApi } from "@/lib/apiClient";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // If returning from Google OAuth, /auth/callback will exchange the session
        // and set the cookies; skip the /me check for now.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        authApi
            .me()
            .then(setUser)
            .catch(() => setUser(null))
            .finally(() => setLoading(false));

        const handleExpired = () => setUser(null);
        window.addEventListener("passport-auth-expired", handleExpired);
        return () => window.removeEventListener("passport-auth-expired", handleExpired);
    }, []);

    const signin = async (email, password) => {
        const { user } = await authApi.signin({ email, password });
        setUser(user);
        return user;
    };
    const signup = async (name, email, password) => {
        // Backend now returns { status: "pending_verification", email } instead of a token
        const res = await authApi.signup({ name, email, password });
        return res; // caller (Auth.jsx) reads res.status to redirect to /verify-email
    };
    const verifyEmail = async (email, otp) => {
        const { user } = await authApi.verifyEmail(email, otp);
        setUser(user);
        return user;
    };
    const applyGoogleSession = async (session_id) => {
        const { user } = await authApi.googleSession(session_id);
        setUser(user);
        return user;
    };
    const signout = async () => {
        try {
            await authApi.logout();
        } finally {
            setUser(null);
        }
    };

    return (
        <AuthCtx.Provider value={{ user, loading, signin, signup, signout, setUser, applyGoogleSession, verifyEmail }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
